import { forwardRef } from 'react';

import { mediaApiUrl, extractParamsFromMediaUrl } from '../lib/mediaApiUrl';
import styled from '@emotion/styled';

import type { MediaApiUrlProps } from '../lib/mediaApiUrl';
import type { MouseEvent, ForwardedRef, DetailedHTMLProps, ImgHTMLAttributes } from 'react';
import { Maybe } from '../types/graphql';

const Img = styled("img")`
  width: 100%;
`;

// images 2x
const xlSource2xMedia =
  '(min-resolution: 192dpi) and (min-width: 1280px),(-webkit-min-device-pixel-ratio: 2) and (min-width: 1280px)';
const lgSource2xMedia =
  '(min-resolution: 192dpi) and (min-width: 1024px),(-webkit-min-device-pixel-ratio: 2) and (min-width: 1024px)';
const mdSource2xMedia =
  '(min-resolution: 192dpi) and (min-width: 768px),(-webkit-min-device-pixel-ratio: 2) and (min-width: 768px)';
const smSource2xMedia =
  '(min-resolution: 192dpi), (-webkit-min-device-pixel-ratio: 2)';

// 1.5X
const xlSource15xMedia =
  '(min-resolution: 144dpi) and (min-width: 1280px),(-webkit-min-device-pixel-ratio: 1.5) and (min-width: 1280px)';
const lgSource15xMedia =
  '(min-resolution: 144dpi) and (min-width: 1024px),(-webkit-min-device-pixel-ratio: 1.5) and (min-width: 1024px)';
const mdSource15xMedia =
  '(min-resolution: 144dpi) and (min-width: 768px),(-webkit-min-device-pixel-ratio: 1.5) and (min-width: 768px)';
const smSource15xMedia =
  '(min-resolution: 144dpi), (-webkit-min-device-pixel-ratio: 1.5)';

// 1X
const xlSource1xMedia = '(min-width: 1280px)';
const lgSource1xMedia = '(min-width: 1024px)';
const mdSource1xMedia = '(min-width: 768px)';

export interface PictureProps extends Partial<Omit<HTMLPictureElement, 'className'>> {
  alt: string;
  url?: string | undefined;
  sm?: number | undefined;
  md?: number | undefined;
  lg?: number | undefined;
  xl?: number | undefined;
  responsiveImages?:
    | {
        smUrl?: string | undefined;
        mdUrl?: string | undefined;
        lgUrl?: string | undefined;
        xlUrl?: string | undefined;
      }
    | undefined;
  className?: string | undefined;
  crop?: string | undefined | null;
  smartCrop?: { width: number; height: number } | undefined;
  ImgCustom?: React.ComponentType | undefined;
  lazyLoad?: boolean | undefined;
  placeholderWidth?: number | undefined;
  rotation?: number | null | undefined;
  onClick?(event: MouseEvent<HTMLImageElement>): void;
  intrinsicDimensions?: { width: Maybe<number> | undefined; height: Maybe<number> | undefined } | undefined;
  'data-testid'?: string | undefined;
  loading?: 'lazy' | 'eager' | undefined | string;
}

export function imageSourceBuilder(
  url: string,
  smartCrop: string | undefined,
  width: number | undefined,
  crop: string | undefined | null,
  format: string | undefined,
  rotation: number | null | undefined
): string {
  const mediaApiUrlProps: MediaApiUrlProps = {
    crop: crop ?? undefined,
    smartCrop,
    format,
    url: url.includes('http') ? url : `https:${url}`,
    width,
  };

  if (rotation) {
    mediaApiUrlProps.rotation = rotation;
  }

  return mediaApiUrl(mediaApiUrlProps);
}

function Picture(
  {
    url: fullUrl,
    alt,
    sm = 480,
    md = 768,
    lg = 1024,
    xl = 1280,
    responsiveImages = {},
    crop,
    smartCrop,
    lazyLoad = false,
    ImgCustom,
    placeholderWidth,
    rotation,
    intrinsicDimensions,
    ...rest
  }: PictureProps,
  ref: ForwardedRef<HTMLPictureElement>
) {
  // sm: > 0px
  // md: > 768px
  // lg: > 1024px
  // xl: > 1280px
  // + @media selectors for 1.5x and 2x pixel density devices

  if (!fullUrl) {
    return null;
  }

  const loading = (lazyLoad ? 'lazy' : 'eager') as DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>['loading']

  if (!fullUrl.match(/xogrp.com|tk-media/)) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
      // @ts-expect-error for some reason typescript is upset about the loading prop despite it being casted to the correct type
        loading={loading}
        alt={alt}
        src={fullUrl}
        {...rest}
      />
    );
  }

  const { url, format } = extractParamsFromMediaUrl(fullUrl);

  if (!url || url.length === 0) {
    return null;
  }

  const {
    smUrl = url,
    mdUrl = url,
    lgUrl = url,
    xlUrl = url,
  } = responsiveImages;

  const ImgComponent = ImgCustom || Img;

  let defaultSmartCrop = '';

  if (smartCrop) {
    defaultSmartCrop = `${smartCrop?.width}.${smartCrop?.height}`;
  }

  const srcsetProp = lazyLoad ? 'data-srcset' : 'srcSet';

  const imgOptions = lazyLoad
    ? {
        loading: 'lazy',
        'data-src': mediaApiUrl({
          url,
          width: md,
          crop: crop ?? undefined,
          format,
          rotation,
        }),
        src: imageSourceBuilder(
          url,
          defaultSmartCrop,
          placeholderWidth || Math.round(sm / 7),
          crop,
          format,
          rotation
        ),
      }
    : {
        src: imageSourceBuilder(
          url,
          defaultSmartCrop,
          md,
          crop,
          format,
          rotation
        ),
      };

  const smartCrop2x =
    smartCrop && `${smartCrop.width * 2}.${smartCrop.height * 2}`;

  const smartCrop15x =
    smartCrop && `${smartCrop.width * 1.5}.${smartCrop.height * 1.5}`;

  // images 2x
  const xlSource2x = imageSourceBuilder(
    xlUrl,
    smartCrop2x,
    xl * 2,
    crop,
    format,
    rotation
  );

  const lgSource2x = imageSourceBuilder(
    lgUrl,
    smartCrop2x,
    lg * 2,
    crop,
    format,
    rotation
  );

  const mdSource2x = imageSourceBuilder(
    mdUrl,
    smartCrop2x,
    md * 2,
    crop,
    format,
    rotation
  );

  const smSource2x = imageSourceBuilder(
    smUrl,
    smartCrop2x,
    sm * 2,
    crop,
    format,
    rotation
  );

  // 1.5X
  const xlSource15x = imageSourceBuilder(
    xlUrl,
    smartCrop15x,
    xl * 1.5,
    crop,
    format,
    rotation
  );

  const lgSource15x = imageSourceBuilder(
    lgUrl,
    smartCrop15x,
    lg * 1.5,
    crop,
    format,
    rotation
  );

  const mdSource15x = imageSourceBuilder(
    mdUrl,
    smartCrop15x,
    md * 1.5,
    crop,
    format,
    rotation
  );

  const smSource15x = imageSourceBuilder(
    smUrl,
    smartCrop15x,
    sm * 1.5,
    crop,
    format,
    rotation
  );

  // 1X
  const xlSource1x = imageSourceBuilder(
    xlUrl,
    defaultSmartCrop,
    xl,
    crop,
    format,
    rotation
  );

  const lgSource1x = imageSourceBuilder(
    lgUrl,
    defaultSmartCrop,
    lg,
    crop,
    format,
    rotation
  );

  const mdSource1x = imageSourceBuilder(
    mdUrl,
    defaultSmartCrop,
    md,
    crop,
    format,
    rotation
  );

  const smSource1x = imageSourceBuilder(
    smUrl,
    defaultSmartCrop,
    sm,
    crop,
    format,
    rotation
  );

  function pictureSource(media: string, source: string) {
    return <source media={media} {...{ [srcsetProp]: source }} />;
  }

  return (
    // @ts-expect-error for some reason typescript is upset about the contentEditable prop despite it being casted to the correct type
    <picture {...rest} ref={ref}>
      {/* >2x pixel density */}
      {pictureSource(xlSource2xMedia, xlSource2x)}
      {pictureSource(lgSource2xMedia, lgSource2x)}
      {pictureSource(mdSource2xMedia, mdSource2x)}
      {pictureSource(smSource2xMedia, smSource2x)}

      {/* >1.5x pixel density */}
      {pictureSource(xlSource15xMedia, xlSource15x)}
      {pictureSource(lgSource15xMedia, lgSource15x)}
      {pictureSource(mdSource15xMedia, mdSource15x)}
      {pictureSource(smSource15xMedia, smSource15x)}

      {/* any pixel density (1x) */}
      {pictureSource(xlSource1xMedia, xlSource1x)}
      {pictureSource(lgSource1xMedia, lgSource1x)}
      {pictureSource(mdSource1xMedia, mdSource1x)}
      <source
        {...{
          [srcsetProp]: smSource1x,
        }}
      />
      {/* Fall-back */}
      <ImgComponent
      // @ts-expect-error for some reason typescript is upset about the loading prop despite it being casted to the correct type
        loading={loading}
        alt={alt}
        {...intrinsicDimensions}
        {...imgOptions}
      />
    </picture>
  );
}

export default forwardRef<HTMLPictureElement, PictureProps>(Picture);
